/**
 * Blog articles — SEO-focused long-form content targeting high-intent
 * South African business-compliance search queries.
 *
 * Add a new article: append an object with { slug, title, description, ... }.
 * `content` is a JSX-friendly string that will be rendered with markdown-lite parsing.
 */

export const articles = [
  {
    slug: 'how-to-register-pty-ltd-south-africa-2026',
    title: 'How to Register a (Pty) Ltd in South Africa — 2026',
    description:
      'The complete, up-to-date walkthrough for registering a private company (Pty) Ltd with CIPC — costs, documents required, and 24-hour turnaround.',
    author: 'Consultium Group',
    datePublished: '2026-02-01',
    heroImage:
      'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=1200&h=600&fit=crop',
    readTime: '6 min read',
    keywords: [
      'register pty ltd south africa',
      'cipc registration 2026',
      'company registration cost',
      'how to register a company in south africa',
    ],
    content: [
      { type: 'h2', text: 'Why Register as a (Pty) Ltd?' },
      {
        type: 'p',
        text:
          'A private company (Pty) Ltd is South Africa\u2019s most popular business structure. It gives you limited liability, a separate legal identity, and the credibility you need to open bank accounts, sign contracts, and pitch for tenders. Registration is handled by the Companies and Intellectual Property Commission (CIPC).',
      },
      { type: 'h2', text: 'What You\u2019ll Need Before You Start' },
      {
        type: 'ul',
        items: [
          'Valid South African ID (green book or smartcard) OR foreign passport for each director',
          'At least one proposed company name (up to 4 is standard)',
          'The physical and postal address of the business',
          'Details of directors and shareholders (name, ID, email, phone, residential address)',
          'A short description of the main business activity',
        ],
      },
      { type: 'h2', text: 'The 5-Step CIPC Registration Process' },
      {
        type: 'ol',
        items: [
          'Name reservation \u2014 CIPC checks your proposed names against existing companies.',
          'Preparation of company documents (MOI, incorporation form CoR14.1).',
          'Submission to CIPC with the R125 filing fee.',
          'CIPC approval (usually 24\u201348 hours \u2014 sometimes same-day).',
          'Delivery of your registration certificate, MOI, and company number.',
        ],
      },
      { type: 'h2', text: 'How Much Does It Cost in 2026?' },
      {
        type: 'p',
        text:
          'CIPC\u2019s official filing fee is R125 for name reservation + R125 for the company registration. Most consultancies (including Consultium Group) charge a bundled service fee \u2014 our full private company registration package starts from R590 all-in, with delivery in 24\u201348 hours.',
      },
      { type: 'h2', text: 'What About SARS and Tax?' },
      {
        type: 'p',
        text:
          'Once your company is registered, CIPC automatically notifies SARS and a corporate income tax reference number is issued. You\u2019ll still need to register for VAT (if turnover exceeds R1 million per year), PAYE (if you employ staff), and to get a tax clearance certificate before bidding on tenders.',
      },
      { type: 'h2', text: 'Ready to Register?' },
      {
        type: 'p',
        text:
          'Consultium Group handles the entire process end-to-end \u2014 name checks, CIPC filing, SARS notification, and delivery of your registration pack. Start your application from our home page or reach out via the contact form.',
      },
    ],
  },
  {
    slug: 'cipc-annual-returns-deadlines-penalties',
    title: 'CIPC Annual Returns 2026: Deadlines, Fees & Penalties',
    description:
      'Every SA company must file an annual return with CIPC. Miss it and your company can be deregistered. Here\u2019s the 2026 fee schedule and deadlines.',
    author: 'Consultium Group',
    datePublished: '2026-01-20',
    heroImage:
      'https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=1200&h=600&fit=crop',
    readTime: '4 min read',
    keywords: [
      'cipc annual return',
      'annual return fees south africa',
      'cipc deregistration',
      'company annual return deadline',
    ],
    content: [
      { type: 'h2', text: 'What Is a CIPC Annual Return?' },
      {
        type: 'p',
        text:
          'An annual return is a mandatory yearly filing that confirms your company still exists and its details are up to date. It has nothing to do with SARS tax returns \u2014 it\u2019s purely a CIPC compliance requirement.',
      },
      { type: 'h2', text: 'When Is It Due?' },
      {
        type: 'p',
        text:
          'Within 30 business days of the anniversary of your company\u2019s incorporation date. So if you registered on 15 March, your annual return window opens on 15 March each year.',
      },
      { type: 'h2', text: '2026 Fee Schedule' },
      {
        type: 'ul',
        items: [
          'Turnover < R1 million: R100',
          'Turnover R1 million \u2013 R10 million: R450',
          'Turnover R10 million \u2013 R25 million: R2\u00a0000',
          'Turnover > R25 million: R3\u00a0000',
        ],
      },
      { type: 'h2', text: 'What Happens If You Miss It?' },
      {
        type: 'p',
        text:
          'CIPC starts deregistration proceedings. A deregistered company can no longer trade, sign contracts, or hold assets \u2014 and reinstating it costs significantly more than filing the return in the first place.',
      },
      { type: 'h2', text: 'Let Us File It for You' },
      {
        type: 'p',
        text:
          'For R450 all-in (CIPC fee included for small companies), Consultium Group files your annual return the same day. Get in touch to check your status.',
      },
    ],
  },
  {
    slug: 'bee-affidavit-vs-certificate-difference',
    title: 'B-BBEE Affidavit vs Certificate: Which One Do You Need?',
    description:
      'EMEs and QSEs can use a free affidavit; larger companies need a full verification certificate. Here\u2019s exactly when each applies.',
    author: 'Consultium Group',
    datePublished: '2026-01-10',
    heroImage:
      'https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=1200&h=600&fit=crop',
    readTime: '5 min read',
    keywords: [
      'bee affidavit vs certificate',
      'eme affidavit south africa',
      'qse b-bbee',
      'level 1 bee certificate',
    ],
    content: [
      { type: 'h2', text: 'The Two BEE Compliance Options' },
      {
        type: 'p',
        text:
          'South Africa\u2019s B-BBEE framework distinguishes between an affidavit (free, self-declared) and a certificate (paid, verified by a SANAS-accredited agency). Which one you need depends entirely on your turnover.',
      },
      { type: 'h2', text: 'Exempted Micro Enterprise (EME) \u2014 Affidavit' },
      {
        type: 'ul',
        items: [
          'Annual turnover: R0 \u2013 R10 million',
          'Automatically qualifies as Level 4 (or Level 1/2 for Black-owned)',
          'No verification needed \u2014 just a sworn affidavit',
          'Cost: Free (just commissioning fee at police station or attorney)',
        ],
      },
      { type: 'h2', text: 'Qualifying Small Enterprise (QSE) \u2014 Affidavit or Certificate' },
      {
        type: 'ul',
        items: [
          'Annual turnover: R10\u00a0m \u2013 R50\u00a0m',
          'Black-owned QSEs can still use an affidavit',
          'Non-Black-owned QSEs need a certificate from a SANAS agency',
        ],
      },
      { type: 'h2', text: 'Generic (Large) Enterprise \u2014 Full Certificate Required' },
      {
        type: 'ul',
        items: [
          'Annual turnover: > R50\u00a0m',
          'Full B-BBEE verification against all five scorecard elements',
          'Certificate valid for 12 months',
        ],
      },
      { type: 'h2', text: 'Get the Right Document, First Time' },
      {
        type: 'p',
        text:
          'Consultium Group prepares B-BBEE affidavits from R390 and refers larger companies to trusted SANAS-accredited partners. Send us your latest annual turnover figure and we\u2019ll confirm which document you need.',
      },
    ],
  },
  {
    slug: 'public-officer-south-africa-sars-guide',
    title: 'Public Officer for Companies: SARS Rules & Appointment',
    description:
      'Every South African company must appoint a Public Officer with SARS within one month of incorporation. Here is who qualifies, what they do, and how to appoint one in 2026.',
    author: 'Consultium Group',
    datePublished: '2026-02-15',
    heroImage:
      'https://images.unsplash.com/photo-1521791136064-7986c2920216?w=1200&h=600&fit=crop',
    readTime: '6 min read',
    keywords: [
      'public officer south africa',
      'appoint public officer sars',
      'sars public officer requirements',
      'company representative sars',
    ],
    content: [
      { type: 'h2', text: 'What Is a Public Officer?' },
      {
        type: 'p',
        text:
          'A Public Officer is the natural person appointed by a company to be its official representative for all matters relating to SARS. Section 246 of the Tax Administration Act, 2011, requires every registered company to appoint one within one month of becoming a taxpayer. Without a Public Officer on record, SARS may refuse to accept returns, issue an eFiling profile, or activate a Tax Compliance Status PIN.',
      },
      { type: 'h2', text: 'Who Can Be Appointed?' },
      {
        type: 'ul',
        items: [
          'A natural person who ordinarily resides in South Africa',
          'A director, member, senior official or company secretary of the company',
          'A person approved by SARS in writing where no eligible resident is available',
          'The person must have full mental capacity and not be under sequestration or a disqualification order',
        ],
      },
      {
        type: 'callout',
        title: 'Foreign-owned companies',
        text:
          'If all your directors live outside South Africa, you cannot appoint them as Public Officer directly. In practice most foreign-owned SA companies appoint a local operations manager, accountant or approved tax practitioner. Consultium Group can act as an approved Public Officer for your company if needed.',
      },
      { type: 'h2', text: 'What Does a Public Officer Actually Do?' },
      {
        type: 'ul',
        items: [
          'Signs and submits ITR14 (annual company income-tax return) and IRP6 (provisional tax) returns',
          'Handles VAT, PAYE, UIF and SDL registration and monthly submissions',
          'Responds to SARS letters, notices, verifications and audits on behalf of the company',
          'Requests a Tax Compliance Status PIN for tenders, bank onboarding or B-BBEE affidavits',
          'Is personally responsible under the Tax Administration Act for the accuracy of what is submitted',
        ],
      },
      { type: 'h2', text: 'How to Appoint or Change a Public Officer' },
      {
        type: 'ol',
        items: [
          'Prepare a signed company resolution (director or shareholder minute) appointing the individual',
          'Complete SARS forms IT77C plus RAV01 (or update via SARS eFiling under "Maintain SARS Registered Details")',
          'Attach a certified copy of the appointee\u2019s ID or passport and proof of residential address (not older than 3 months)',
          'Attach the CIPC company registration certificate and CoR 14.3 (or CK1/CK2 for close corporations)',
          'Book a SARS branch appointment or upload through the Online Query System, then wait for SARS to update the RAV01 profile',
        ],
      },
      { type: 'h2', text: 'Common Reasons SARS Rejects the Appointment' },
      {
        type: 'ul',
        items: [
          'The appointee is not a resident of South Africa and no SARS approval was obtained',
          'ID or passport copy is not certified or is more than 3 months old',
          'Proof of address does not match the ID name or is outdated',
          'The company resolution is not signed by all directors (or does not reference the correct company registration number)',
          'The individual already has an active Public Officer role at another company under audit',
        ],
      },
      { type: 'h2', text: 'What Happens If You Miss the 30-Day Window?' },
      {
        type: 'p',
        text:
          'SARS may impose administrative non-compliance penalties, refuse to activate tax types (VAT, PAYE) and block Tax Compliance Status verifications. Where the delay is material, SARS can also raise estimated assessments and start collection steps. In practice, appointing a Public Officer late is straightforward once the required documents are in order, but the company remains liable for any penalties accrued in the interim.',
      },
      { type: 'h2', text: 'Let Consultium Handle It' },
      {
        type: 'p',
        text:
          'We prepare the resolution, complete the SARS forms, submit the RAV01 update and follow up until the appointment reflects on your eFiling profile. Where you have no eligible resident director, we can also act as your approved Public Officer under a formal service agreement. Start on the homepage services list or contact our team.',
      },
    ],
  },
  {
    slug: 'dormant-company-tax-returns-nil-filing',
    title: 'Dormant Company Tax Returns: Filing Nil Returns 2026',
    description:
      'A dormant SA company still has to file SARS returns. Here is how to submit nil ITR14 and IRP6 returns, and when to consider deregistration instead.',
    author: 'Consultium Group',
    datePublished: '2026-02-10',
    heroImage:
      'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=1200&h=600&fit=crop',
    readTime: '5 min read',
    keywords: [
      'dormant company tax return south africa',
      'nil itr14',
      'dormant cc sars',
      'deregister dormant company',
    ],
    content: [
      { type: 'h2', text: 'What Is a Dormant Company?' },
      {
        type: 'p',
        text:
          'A company is dormant for tax purposes when it has had no gross income, no expenses claimed for tax, and no asset disposals during the year of assessment. Being dormant does not mean SARS has forgotten about you — every active taxpayer is expected to submit at least a nil return until they are formally deregistered as a taxpayer or the company is deregistered at CIPC.',
      },
      {
        type: 'callout',
        title: 'Dormant vs. deregistered',
        text:
          'Dormant means the company still exists at CIPC and remains a SARS taxpayer, but is not trading. Deregistered means CIPC has removed the company from the register altogether. Only deregistration ends the obligation to file company tax returns.',
      },
      { type: 'h2', text: 'What Returns Does a Dormant Company Still File?' },
      {
        type: 'table',
        headers: ['Return', 'When', 'What to submit'],
        rows: [
          ['ITR14 (income tax)', 'Annually, within 12 months of financial year-end', 'Nil return with zero income, zero deductions and zero taxable income'],
          ['IRP6 (provisional tax)', '1st period: 6 months in; 2nd period: at year-end', 'Nil return unless you expect income in the current year'],
          ['EMP201 (payroll)', 'Only if registered as an employer', 'Nil declaration until the employer registration is closed'],
          ['VAT201', 'Only if VAT-registered', 'Nil declaration until VAT registration is cancelled via VAT123'],
          ['CIPC Annual Return', 'Yearly on incorporation anniversary', 'Even dormant companies pay this (as low as R100 for turnover under R1m)'],
        ],
      },
      { type: 'h2', text: 'Step-by-Step: Filing a Nil ITR14' },
      {
        type: 'ol',
        items: [
          'Log in to SARS eFiling with the Public Officer profile',
          'Under Returns → Company/Trust → ITR14, select the correct year of assessment',
          'On the wizard, answer Yes to "Is the company dormant?" — this collapses most schedules to zero',
          'Confirm the financial year-end, capture zero income and zero expenditure',
          'Submit and download the ITA34 (assessment) as proof of filing',
        ],
      },
      { type: 'h2', text: 'Should You Deregister Instead?' },
      {
        type: 'p',
        text:
          'If the company has been dormant for more than three financial years and you have no plans to trade under it, CIPC deregistration is often the more sensible option. It costs R0 to R150 and stops the ongoing SARS and CIPC compliance burden. However, deregistration is not appropriate if the company holds bank accounts, contracts, tenders, licences, or if it may be reactivated in the future.',
      },
      { type: 'h2', text: 'Common Mistakes to Avoid' },
      {
        type: 'ul',
        items: [
          'Assuming nil returns are optional — SARS still applies administrative penalties for missing returns',
          'Filing the ITR14 without also filing the two IRP6 provisional returns',
          'Forgetting the CIPC annual return, which can trigger deregistration for a different reason',
          'Not de-registering VAT / PAYE if the company will remain dormant indefinitely',
        ],
      },
      { type: 'h2', text: 'How We Help' },
      {
        type: 'p',
        text:
          'Consultium Group files nil ITR14 and IRP6 returns for dormant companies from R450 each, or handles a full deregistration package (CIPC deregistration plus final SARS closure) end-to-end. Send us your company number and financial year-end and we will confirm exactly what is outstanding.',
      },
    ],
  },
  {
    slug: 'foreign-director-company-registration-sa',
    title: 'Foreign Directors: Registering a Company in South Africa',
    description:
      'Foreign nationals can register a South African (Pty) Ltd remotely. Here are the documents, ID requirements, tax implications and common pitfalls for 2026.',
    author: 'Consultium Group',
    datePublished: '2026-02-05',
    heroImage:
      'https://images.unsplash.com/photo-1524749292158-7540c2494485?w=1200&h=600&fit=crop',
    readTime: '7 min read',
    keywords: [
      'foreign director company registration south africa',
      'register sa company as foreigner',
      'non-resident director cipc',
      'foreign shareholder pty ltd',
    ],
    content: [
      { type: 'h2', text: 'Can Foreigners Register a Company in South Africa?' },
      {
        type: 'p',
        text:
          'Yes. The Companies Act, 2008, allows any person — regardless of nationality or residency — to be a director, shareholder or incorporator of a South African private company. You do not need a work permit or business visa to hold shares, receive dividends or sit on the board. What you do need is a valid passport, a South African registered address, and (in almost all cases) a South African-resident Public Officer for SARS purposes.',
      },
      { type: 'h2', text: 'The Documents You Will Need' },
      {
        type: 'ul',
        items: [
          'Certified colour copy of every foreign director\u2019s passport (certified within the last 3 months by a notary, commissioner of oaths or SA embassy)',
          'Proof of residential address for each foreign director — utility bill, bank statement or lease dated within the last 3 months, translated into English if not already',
          'The proposed South African company name (up to four choices)',
          'A South African registered address (may be a shared services address or the address of your local accountant)',
          'Contact details for each director: email, mobile, physical address',
          'One-line description of the intended main business activity',
        ],
      },
      {
        type: 'callout',
        title: 'Certification abroad',
        text:
          'CIPC accepts documents certified by a notary or the South African embassy in the director\u2019s country. Apostille or superlegalisation may be required in some jurisdictions — Consultium can advise for your specific country before you sign anything.',
      },
      { type: 'h2', text: 'The Registration Process for Non-Residents' },
      {
        type: 'ol',
        items: [
          'Send your documents electronically (secure upload) to our team',
          'We reserve up to four proposed names with CIPC',
          'We prepare a Memorandum of Incorporation (MOI) suitable for a foreign-owned (Pty) Ltd',
          'You sign digitally or via a witnessed courier package — no travel is required',
          'CIPC issues the registration certificate and company number within 24\u201348 hours',
          'We activate SARS income tax registration and appoint a compliant Public Officer',
        ],
      },
      { type: 'h2', text: 'Tax and Exchange Control Implications' },
      {
        type: 'p',
        text:
          'A South African (Pty) Ltd owned by foreign shareholders is still a South African tax resident and pays 27% corporate income tax on its worldwide profits. Dividends paid to non-resident shareholders are subject to 20% Dividend Withholding Tax (reducible by double-taxation agreements). If you plan to remit funds in or out of South Africa, you will also need a South African bank account and, depending on the amounts, SARB (South African Reserve Bank) exchange control approval.',
      },
      { type: 'h2', text: 'Do You Need a Business Visa?' },
      {
        type: 'p',
        text:
          'Owning shares or being a director does not require a visa. However, if you plan to physically work in South Africa on behalf of the company (e.g. run day-to-day operations from the country), you will need a business visa, critical skills visa, or intra-company transfer visa depending on the circumstances. Consultium can refer you to trusted immigration specialists once your company is registered.',
      },
      { type: 'h2', text: 'Common Pitfalls' },
      {
        type: 'ul',
        items: [
          'Using an uncertified passport copy — CIPC rejects the application on the spot',
          'Nominating a foreign director as Public Officer without a resident backup — SARS rejects the appointment',
          'Choosing a company name that includes a protected term (e.g. "bank", "insurance") without regulator approval',
          'Not appointing a local company secretary where the MOI requires one',
          'Missing the first CIPC annual return because the registered address does not forward mail correctly',
        ],
      },
      { type: 'h2', text: 'Speak to Us Before You Sign' },
      {
        type: 'p',
        text:
          'A short call with our team before you send any documents saves foreign directors an enormous amount of time — we confirm which certifications your country accepts, whether your intended activity needs a sector regulator (banking, mining, education), and whether a branch of a foreign company might be a better structure than a (Pty) Ltd for your situation. Reach us on the contact page or via WhatsApp.',
      },
    ],
  },
  {
    slug: 'beneficial-ownership-filing-cipc-2026',
    title: 'Beneficial Ownership Filing at CIPC: 2026 Guide',
    description:
      'Every SA company must keep a beneficial ownership register and file it with CIPC. Here is who counts as a beneficial owner, how to file, and what happens if you miss it.',
    author: 'Consultium Group',
    datePublished: '2026-01-28',
    heroImage:
      'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=1200&h=600&fit=crop',
    readTime: '5 min read',
    keywords: [
      'beneficial ownership cipc',
      'boo declaration south africa',
      'ultimate beneficial owner sa',
      'company disclosure act south africa',
    ],
    content: [
      { type: 'h2', text: 'Why Beneficial Ownership Filings Exist' },
      {
        type: 'p',
        text:
          'Following South Africa\u2019s greylisting by the Financial Action Task Force (FATF) in February 2023, the General Laws Amendment Act, 2022, introduced beneficial ownership disclosure obligations for all companies, close corporations and non-profit companies. Since 24 May 2023, CIPC has required every entity to keep and file a beneficial ownership register — and to update it within 10 business days of any change.',
      },
      { type: 'h2', text: 'Who Counts as a Beneficial Owner?' },
      {
        type: 'p',
        text:
          'A beneficial owner is a natural person who ultimately owns or controls a company, directly or indirectly. The Companies Act defines "ownership" as holding 5% or more of the shares or voting rights. Control includes the ability to appoint or remove directors, veto major decisions, or exercise dominant influence over the company. Legal entities (holding companies, trusts) are not beneficial owners themselves — you must trace ownership up through them to the natural persons at the top.',
      },
      {
        type: 'callout',
        title: 'Affected entities that must file',
        text:
          'All companies (private, public, personal liability, state-owned), close corporations, non-profit companies (NPCs) and co-operatives registered at CIPC. Trusts file separately with the Master of the High Court under a similar regime.',
      },
      { type: 'h2', text: 'What You Have to Keep in the Register' },
      {
        type: 'ul',
        items: [
          'Full names, ID / passport number, nationality and date of birth of each beneficial owner',
          'Residential address and email address',
          'Nature of the interest (shares, voting rights, control, other)',
          'Extent of the interest (percentage held, indirect chain, date acquired)',
          'Supporting proof: certified ID / passport copies and, where relevant, share certificates or trust deeds',
        ],
      },
      { type: 'h2', text: 'How to File at CIPC' },
      {
        type: 'ol',
        items: [
          'Log in to the CIPC e-services portal with an authorised customer profile',
          'Under "Beneficial Ownership", select the correct entity by registration number',
          'Complete the online declaration and upload each supporting document (certified copies, share register)',
          'Pay the filing fee (R0 for the initial filing, R100 for updates in some cases)',
          'Download the confirmation and keep it with your statutory records',
        ],
      },
      { type: 'h2', text: 'When Must You Update?' },
      {
        type: 'ul',
        items: [
          'Within 10 business days of any change to the beneficial ownership register',
          'Annually, alongside the CIPC annual return — CIPC now requires a fresh beneficial ownership submission with every return',
          'Whenever a new director or shareholder is appointed, resigned or transferred more than 5% of the issued shares',
        ],
      },
      { type: 'h2', text: 'Penalties for Non-Compliance' },
      {
        type: 'p',
        text:
          'CIPC can impose administrative fines, reject the annual return, and refer wilful non-compliance to the National Prosecuting Authority. The Companies Act, as amended, provides for penalties of up to 10% of turnover or R1 million (whichever is greater) for serious breaches. Practically, most companies are first issued a compliance notice and have 60 days to remedy the position.',
      },
      { type: 'h2', text: 'Let Us File It Correctly' },
      {
        type: 'p',
        text:
          'Consultium Group prepares the beneficial ownership register, drafts director and shareholder declarations, uploads the filing at CIPC, and stores your certified documents securely. Our package starts at R390 for straightforward structures and includes annual updates. Contact us with your company registration number to get started.',
      },
    ],
  },
  {
    slug: 'tax-clearance-certificate-tcs-pin-sa',
    title: 'SARS Tax Clearance (TCS PIN) 2026: How to Get Yours',
    description:
      'A Tax Compliance Status (TCS) PIN replaces the old Tax Clearance Certificate. Here is exactly how to request one, why it fails, and what buyers actually check.',
    author: 'Consultium Group',
    datePublished: '2026-01-15',
    heroImage:
      'https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=1200&h=600&fit=crop',
    readTime: '6 min read',
    keywords: [
      'sars tax clearance',
      'tcs pin south africa',
      'tax compliance status pin',
      'tax clearance certificate 2026',
    ],
    content: [
      { type: 'h2', text: 'From Tax Clearance Certificate to TCS PIN' },
      {
        type: 'p',
        text:
          'SARS phased out the paper Tax Clearance Certificate in 2016 and replaced it with the Tax Compliance Status (TCS) system on eFiling. Instead of a signed certificate, you now receive a unique PIN which any third party — a client, funder, tender authority or bank — can enter on the SARS website to see your compliance status in real time. The PIN is valid for 12 months but the status behind it updates whenever your compliance position changes.',
      },
      { type: 'h2', text: 'What "Tax Compliant" Actually Means' },
      {
        type: 'ul',
        items: [
          'All required returns (ITR14, IRP6, EMP201, VAT201) are submitted and up to date',
          'No outstanding tax debt over R100 (excluding SARS-approved payment arrangements)',
          'All tax types you are registered for reflect the correct registered particulars (registered address, Public Officer, banking details)',
          'You are not under a criminal tax investigation or a section 172 preservation order',
        ],
      },
      {
        type: 'callout',
        title: 'The four request types',
        text:
          'When you request a TCS PIN you must choose the correct purpose: Good Standing (day-to-day compliance), Tender, Foreign Investment Allowance (FIA), or Emigration. Requesting the wrong type is one of the most common reasons a PIN is rejected — pick the option that matches what the third party actually asked for.',
      },
      { type: 'h2', text: 'Step-by-Step: Requesting a TCS PIN' },
      {
        type: 'ol',
        items: [
          'Log in to SARS eFiling under the taxpayer\u2019s profile (individual, company or trust)',
          'Go to Tax Status → Activation → activate Tax Compliance Status for the entity',
          'Under Tax Status → Tax Compliance Status Request, choose the purpose (Good Standing, Tender, FIA, Emigration)',
          'Confirm all the compliance items in the summary — resolve any red items before continuing',
          'Submit the request and, if compliant, SARS issues the PIN immediately',
          'Share the PIN with your third party — they verify it on the SARS Verify TCS page',
        ],
      },
      { type: 'h2', text: 'Why TCS Requests Fail — And What to Do' },
      {
        type: 'table',
        headers: ['Reason', 'What SARS shows', 'Fix'],
        rows: [
          ['Outstanding return', 'Red flag on the return type', 'Submit the return (even nil), wait 24\u201348 hours, retry'],
          ['Outstanding debt', 'Non-compliant', 'Pay in full or set up a Section 89 payment arrangement'],
          ['Registered details mismatch', 'Purple flag', 'Update RAV01 (address, banking, Public Officer)'],
          ['Estimated assessment (ITA34) outstanding', 'Non-compliant', 'File the actual return to replace the estimate'],
          ['Under audit / verification', 'Purple flag', 'Respond to the SARS letter with all supporting documents'],
        ],
      },
      { type: 'h2', text: 'How Long Is the PIN Valid?' },
      {
        type: 'p',
        text:
          'A PIN itself is valid for 12 months, but the compliance status behind it is live. That means if your compliance status changes (for example a new return becomes overdue), a third party checking your PIN today will see "Non-Compliant" even though the PIN itself is still within its 12-month window. In practice you should monitor your TCS at least quarterly and fix any red flags as soon as they appear.',
      },
      { type: 'h2', text: 'When You Absolutely Need a PIN' },
      {
        type: 'ul',
        items: [
          'Bidding on any government or SOE tender (Tender purpose)',
          'Bank onboarding for a new business account or loan (Good Standing)',
          'Applying for a B-BBEE affidavit for QSE / Generic verification',
          'Emigrating or transferring more than R1 million offshore (FIA or Emigration purpose)',
          'Renewing certain sector licences (liquor, financial services, gaming)',
        ],
      },
      { type: 'h2', text: 'How We Help' },
      {
        type: 'p',
        text:
          'Consultium Group audits your SARS compliance before the request, resolves any outstanding returns or registration issues, submits the TCS request in the correct category, and monitors your status monthly. Our tax clearance package starts at R450 for standard companies. Get in touch with your company registration number and we will provide a compliance report within one working day.',
      },
    ],
  },
  {
    slug: 'state-of-sa-company-compliance-2026',
    title: 'State of SA Company Compliance 2026: The Numbers',
    description:
      'How many companies exist in South Africa? How many get deregistered? What percentage actually pay tax? A data-driven look at the CIPC, SARS and FATF landscape in 2026.',
    author: 'Consultium Group',
    datePublished: '2026-02-20',
    dateModified: '2026-02-20',
    heroImage:
      'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=1200&h=600&fit=crop',
    readTime: '12 min read',
    keywords: [
      'south africa company compliance statistics',
      'cipc register active companies',
      'sars corporate tax statistics 2024',
      'fatf greylist south africa exit',
      'beneficial ownership disclosures cipc',
    ],
    content: [
      {
        type: 'callout',
        title: 'Executive summary',
        text:
          'South Africa\u2019s formal business register held 2.95 million active entities as of August 2026. Yet SARS only assessed 1.17 million companies for the 2022 tax year, and just 20.7% declared a positive taxable income. Over one million companies were referred for deregistration in the 2023\u20132025 clean-up. Following exit from the FATF greylist in October 2025, CIPC beneficial ownership compliance now sits above 1.1 million disclosures. This report unpacks what the data means for founders, directors and finance teams operating in South Africa in 2026.',
      },
      { type: 'h2', text: 'How Big Is the South African Company Register?' },
      {
        type: 'p',
        text:
          'The Companies and Intellectual Property Commission (CIPC) is the statutory register for South African companies and co-operatives. In its most recent parliamentary reply the CIPC confirmed 2,946,848 active entities on the register as of August 2026 — comprising private companies, personal liability companies, non-profit companies, close corporations and co-operatives.',
      },
      {
        type: 'table',
        headers: ['Metric', 'Figure', 'Source'],
        rows: [
          ['Active entities on the CIPC register', '2,946,848', 'CIPC (Aug 2026, PMG reply)'],
          ['New companies + co-operatives registered in 2024', '497,052', 'CIPC 2024 calendar year'],
          ['New companies + co-operatives registered in 2025', '542,165', 'CIPC 2025 calendar year'],
          ['New companies + co-operatives in FY 2024/25', '493,977', 'CIPC 2024/25 Annual Report'],
        ],
      },
      {
        type: 'p',
        text:
          'Registration volume grew 9.1% between the 2024 and 2025 calendar years. In a country of roughly 62 million people that means one new company was registered for every 114 South Africans in 2025 alone — a level of formal-sector formation that is very high by developed-economy standards, but which also reflects a large tail of "shelf" entities that never trade.',
      },
      { type: 'h2', text: 'The Deregistration Mountain' },
      {
        type: 'p',
        text:
          'CIPC uses deregistration as its primary tool to keep the register accurate. Two large clean-up waves have run in the current cycle:',
      },
      {
        type: 'ul',
        items: [
          'On 30 March 2023, CIPC deregistered 647,853 companies and close corporations that had failed to file annual returns for more than three consecutive years.',
          'In November 2024 the Commission announced a fresh drive against approximately 500,000 non-compliant entities. Referral for deregistration ran in early December 2024 and final deregistration for those still non-compliant closed at the end of February / early March 2025.',
        ],
      },
      {
        type: 'callout',
        title: 'What "deregistered" really means',
        text:
          'A deregistered company loses its legal personality. It cannot contract, hold bank accounts, sign leases, or bid on tenders. Directors who continue to operate through a deregistered entity attract personal liability under section 22 of the Companies Act. Reinstatement is possible via a CoR40.5 application, but is materially more expensive than filing the outstanding annual returns would have been.',
      },
      {
        type: 'p',
        text:
          'Roughly 1.15 million referrals for deregistration in a 24-month window against a register of about 3 million tells you two things: first, that between 30% and 40% of the entities on the CIPC register at any moment are not actively meeting basic compliance obligations. Second, that operating a company in South Africa without an ongoing filing partner is a demonstrable business risk.',
      },
      { type: 'h2', text: 'How Many Companies Actually Trade or Pay Tax?' },
      {
        type: 'p',
        text:
          'SARS\u2019 17th annual Tax Statistics publication (released 10 December 2024) covers the 2022 tax year (assessed by 31 August 2024). It provides the most complete recent picture of corporate activity in the country.',
      },
      {
        type: 'table',
        headers: ['Corporate income tax breakdown (2022 tax year)', 'Share', 'Companies'],
        rows: [
          ['Assessed companies (total)', '100.0%', '1,166,692'],
          ['Positive taxable income', '20.7%', '241,505 (approx.)'],
          ['Zero taxable income (breakeven / dormant)', '54.6%', '637,014 (approx.)'],
          ['Assessed loss', '24.7%', '288,173 (approx.)'],
          ['Large companies with taxable income > R200m', '0.05%', '549'],
        ],
      },
      {
        type: 'callout',
        title: 'Where the tax actually comes from',
        text:
          'Those 549 large companies — 0.2% of positive-income companies and about 0.05% of all assessed companies — contributed 66.5% of all Corporate Income Tax assessed. The financial intermediation, insurance, real estate and business services sector alone accounted for 32.8% of total CIT assessed, from 22.8% of the companies.',
      },
      {
        type: 'p',
        text:
          'The 54.6% zero-taxable-income group is the largest single bucket. Some are genuinely dormant. Some are pre-revenue start-ups. Many are legitimately trading but operating close to breakeven. Regardless of the reason, every one of them still has to file an ITR14 and, in most cases, two IRP6 provisional returns each year, plus the CIPC annual return.',
      },
      { type: 'h2', text: 'The VAT Vendor Register' },
      {
        type: 'p',
        text:
          'The 2024/25 fiscal year Tax Statistics show 900,285 registered VAT vendors, of which just 496,858 were classified as active. Companies and close corporations made up 82.5% of active vendors but contributed 93.8% of domestic VAT payments — again, a heavily concentrated tax base.',
      },
      {
        type: 'table',
        headers: ['VAT vendor status (2024/25)', 'Figure'],
        rows: [
          ['Total registered vendors', '900,285'],
          ['Active vendors', '496,858 (55.2%)'],
          ['Companies / CCs share of active vendors', '82.5%'],
          ['Companies / CCs share of domestic VAT payments', '93.8%'],
        ],
      },
      {
        type: 'p',
        text:
          'The gap between "registered" and "active" tells the same story as the CIPC data: a lot of formal-sector businesses that got started never traded meaningfully, or stopped trading without properly deregistering. That is the compliance debt that catches directors out when they later try to open a bank account, apply for a tender or emigrate.',
      },
      { type: 'h2', text: 'FATF Greylist Exit and Beneficial Ownership' },
      {
        type: 'p',
        text:
          'South Africa was placed on the FATF greylist in February 2023 after 22 strategic deficiencies were identified in its Anti-Money Laundering and Combating Financing of Terrorism regime. On 24 October 2025 the FATF Plenary in Paris formally removed South Africa from the list following an on-site assessment in Pretoria (29\u201330 July 2025) confirming that reforms were both implemented and sustainable.',
      },
      {
        type: 'p',
        text:
          'A major driver of that exit was CIPC\u2019s beneficial ownership programme. By February 2025 the CIPC reported over 1.1 million beneficial ownership disclosures on file — versus roughly zero before the Companies Amendment Act obligations kicked in on 24 May 2023.',
      },
      {
        type: 'callout',
        title: 'What has changed for directors',
        text:
          'Every company, close corporation, NPC and co-operative must now (a) keep a beneficial ownership register at its registered office, (b) file it with CIPC, (c) update the filing within 10 business days of any change in ownership or control, and (d) submit a fresh beneficial ownership declaration alongside every annual return. The next FATF Mutual Evaluation begins in early 2026 and concludes in October 2027, so enforcement of these obligations is expected to tighten, not loosen.',
      },
      { type: 'h2', text: 'What This Means for Founders and Finance Teams' },
      {
        type: 'ul',
        items: [
          'A "shelf" or unused company is not a low-risk asset. If left uncompliant it becomes a demonstrable liability and eventually a reputational risk for its directors.',
          'The most common single reason for tender rejection remains a non-compliant Tax Compliance Status. Since the TCS PIN is now live, third parties see your compliance state in real time — the annual filing rhythm matters more than ever.',
          'The 54.6% zero-taxable-income cohort proves that filing nil returns is a mainstream, not an exceptional, activity. It is also fully automatable — the cost of getting it wrong (administrative penalties, estimated assessments) is materially higher than the cost of doing it right.',
          'Post-greylist enforcement is likely to focus on the accuracy of beneficial ownership data, not just whether a submission was made. Directors should audit their filed BO information against their share register at least annually.',
          'If your company holds a bank account, contracts, employees, licences or intellectual property, treating deregistration as a "silent" outcome is dangerous. Bank accounts freeze, contracts fall away, and employees lose their PAYE trail.',
        ],
      },
      { type: 'h2', text: 'How Consultium Group Reads the Data' },
      {
        type: 'p',
        text:
          'The picture is not one of a hostile compliance environment — SARS collections continue to grow and CIPC turnaround times have improved meaningfully since the launch of BizPortal. Rather, it is one of an increasingly automated, real-time compliance system that is unforgiving of neglect. Filing calendars, current registered particulars, an appointed Public Officer and up-to-date beneficial ownership records are now the baseline for any company that wants access to bank finance, tenders, cross-border payments or a clean sale.',
      },
      {
        type: 'p',
        text:
          'Consultium Group works with over a thousand South African companies each year to keep them on the right side of these numbers. If you would like a diagnostic of your own CIPC and SARS position — no obligation, one working day turnaround — reach out through the contact page or by WhatsApp.',
      },
      { type: 'h2', text: 'Methodology and Sources' },
      {
        type: 'p',
        text:
          'Every statistic in this report is drawn from a named public source. Where two publications give slightly different figures (for example CIPC calendar-year versus fiscal-year new registrations) both figures are shown in the table. Figures have been rounded to the nearest whole number where percentages were multiplied out to derive companies.',
      },
      {
        type: 'ul',
        items: [
          'CIPC — Parliamentary reply on active companies, 2026 (Parliamentary Monitoring Group).',
          'CIPC Annual Report 2023/24 and 2024/25 (Table G.3 on deregistrations).',
          'CIPC Media Statement — Deregistration for Non-Compliance, November 2024.',
          'CIPC Notice on Number of Deregistered Companies, 31 March 2023 (647,853 entities).',
          'SARS + National Treasury — Joint Media Statement on the 17th Annual Tax Statistics, 10 December 2024.',
          'SARS + National Treasury — 18th Annual Tax Statistics (VAT vendor 2024/25 figures).',
          'National Treasury — Media Statement on FATF Greylist Exit, 24 October 2025.',
          'IOL Business Report — South Africa nears greylist exit, 20 February 2025 (CIPC 1.1m BO disclosures).',
        ],
      },
      {
        type: 'callout',
        title: 'Cite this report',
        text:
          'Consultium Group. (2026). State of SA Company Compliance 2026: The Numbers. https://consultiumgroup.co.za/blog/state-of-sa-company-compliance-2026',
      },
    ],
  },
];

export const getArticle = (slug) => articles.find((a) => a.slug === slug);
